// src/components/dashboard/PowerGenerationSystemSize.js
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from "chart.js";
import { DASHBOARD_COLORS, getEnergyColor } from "../../config/colorScheme";
import { useElcCapByTechnology } from "../../hooks/useElcCapByTechnology";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// energy color mapping
const colorForTech = (key, variant = "primary") => {
  const map = {
    offshoreWindFarms: "offshorewind",
    onshoreWindFarms: "onshorewind",
    solarFarms: "solar",
    tidalEnergyProjects: "tidal",
    hydropowerPlants: "hydro",
    biomassPowerStations: "biomass",
    nuclearMicroPlants: "nuclear",
    "Offshore Wind": "offshoreWind",
    "Onshore Wind": "onshoreWind",
    "Solar PV": "solar",
    Tidal: "tidal",
    Wave: "tidal",
    Hydropower: "hydro",
    Hydrogen: "hydrogen",
    Biomass: "biomass",
    BECCS: "beccs",
    Interconnector: "interconnector",
    Nuclear: "nuclear",
    Gas: "gas",
    "Gas with CCS": "gasCcs",
    Coal: "coal",
    Oil: "oil",
    CHP: "chp",
    Other: "other",
  };
  const name = map[key];
  return name ? getEnergyColor(name, variant) : DASHBOARD_COLORS?.neutral?.baseline?.[variant] ?? "#6b7280";
};

// human labels
const labelForTech = (key) =>
  ({
    offshoreWindFarms: "Offshore Wind",
    onshoreWindFarms: "Onshore Wind",
    solarFarms: "Solar",
    tidalEnergyProjects: "Tidal / Wave",
    hydropowerPlants: "Hydropower",
    biomassPowerStations: "Biomass",
    nuclearMicroPlants: "Nuclear",
  }[key] || key);

// formatters
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—");
const fmtInt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "—");
const fmtMillions = (n) => {
  if (!Number.isFinite(n)) return "—";
  const millions = n / 1_000_000;
  return millions >= 100
    ? `${Math.round(millions).toLocaleString()}M`
    : `${millions.toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
};

// --- conversion rules (GW → units) -----------------------------------------
// Capacities per unit (future typicals)
const MW_PER_UNIT = {
  OFF_WIND: 14,     // MW per offshore turbine
  ON_WIND:  3,      // MW per onshore turbine
  SOLAR:    0.0004, // MW per panel (400 W)
  NUCLEAR:  1600,   // MW per reactor
  BIOMASS:  600,    // MW per unit (e.g., Drax)
};

// Map backend tech name → canonical “kind” we can convert
const techKindForCounts = (name = "") => {
  const s = String(name).toLowerCase();
  if (s.includes("offshore") && s.includes("wind")) return "OFF_WIND";
  if (s.includes("onshore") && s.includes("wind"))  return "ON_WIND";
  if (s.includes("solar") || s.includes("pv"))      return "SOLAR";
  if (s.includes("nuclear"))                         return "NUCLEAR";
  // Count BECCS and Biomass together as “biomass units”
  if (s.includes("biomass") || s.includes("beccs")) return "BIOMASS";
  // Drop tidal/wave/hydro by returning null
  if (s.includes("tidal") || s.includes("wave") || s.includes("hydro")) return null;
  return null;
};

// ---------------------------------------------------------------------------

export default function PowerGenerationSystemSize({
  scenario,
  from = 2025,
  to = 2050,
  title = "Electricity System Size (Capacity, GW)",
  className = "bg-white p-6 sm:p-2 md:p-2 rounded-lg shadow-md w-full",
  gwScale = 1, // if your PV is MW, pass 1/1000
}) {
  const { loading, error, labels, byTechYear, techList } =
    useElcCapByTechnology({ scenario, from, to, gwScale });

  // Build stacked datasets (GW)
  const datasets = useMemo(() => {
    return techList.map((tech) => {
      const data = labels.map((y) => byTechYear[tech]?.[y] ?? 0);
      return {
        label: labelForTech(tech),
        data,
        backgroundColor: colorForTech(tech, "light"),
        borderColor: colorForTech(tech, "primary"),
        borderWidth: 1,
        stack: "cap",
      };
    });
  }, [labels, byTechYear, techList]);

  const chartData = { labels: labels.map(String), datasets };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: true, text: title, font: { size: 16 } },
      legend: { position: "bottom", labels: { boxWidth: 18, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)} GW`,
        },
      },
    },
    scales: {
      x: { stacked: true, title: { display: true, text: "Year" } },
      y: {
        stacked: true,
        max:300,

        title: { display: true, text: "Capacity (GW)" },
        ticks: { callback: (v) => fmt(v) },
      },
    },
  };

  // Delta (last vs first) aggregated by “kind” for counts
  const firstYear = labels[0];
  const lastYear  = labels[labels.length - 1];

  const counts = useMemo(() => {
    if (!firstYear || !lastYear) return null;

    // Sum capacities per kind for first & last year (in GW)
    const sumByKind = (year) => {
      const acc = { OFF_WIND: 0, ON_WIND: 0, SOLAR: 0, NUCLEAR: 0, BIOMASS: 0 };
      for (const tech of techList) {
        const kind = techKindForCounts(tech);
        if (!kind) continue;
        const gw = byTechYear[tech]?.[year] ?? 0;
        acc[kind] += gw;
      }
      return acc;
    };

    const a = sumByKind(firstYear);
    const b = sumByKind(lastYear);

    const deltaGW = {
      OFF_WIND:  Math.max(0, b.OFF_WIND - a.OFF_WIND),
      ON_WIND:   Math.max(0, b.ON_WIND  - a.ON_WIND),
      SOLAR:     Math.max(0, b.SOLAR    - a.SOLAR),
      NUCLEAR:   Math.max(0, b.NUCLEAR  - a.NUCLEAR),
      BIOMASS:   Math.max(0, b.BIOMASS  - a.BIOMASS),
    };

    // Convert GW deltas → unit counts
    const gwToUnits = (gw, kind) => {
      const mwPerUnit = MW_PER_UNIT[kind];
      if (!mwPerUnit || gw <= 0) return 0;
      const mw = gw * 1000;
      return mw / mwPerUnit;
    };

    return {
      years: { firstYear, lastYear },
      // raw deltas (GW)
      deltaGW,
      // unit counts (rounded sensibly when we render)
      units: {
        OFF_WIND: gwToUnits(deltaGW.OFF_WIND, "OFF_WIND"),
        ON_WIND:  gwToUnits(deltaGW.ON_WIND,  "ON_WIND"),
        SOLAR:    gwToUnits(deltaGW.SOLAR,    "SOLAR"),
        NUCLEAR:  gwToUnits(deltaGW.NUCLEAR,  "NUCLEAR"),
        BIOMASS:  gwToUnits(deltaGW.BIOMASS,  "BIOMASS"),
      },
    };
  }, [labels, byTechYear, techList, firstYear, lastYear]);

  const isEmpty =
    !labels?.length ||
    !datasets?.length ||
    datasets.every((d) => (d.data || []).every((v) => v === 0));

  return (
    <div className={className}>
      <div className="h-96 sm:w-5/5 md:w-4/5 lg:w-4/5 xl:w-3/5 align-center m-auto">
        {loading ? (
          <div className="w-full h-full grid place-items-center text-sm text-gray-500">Loading capacity…</div>
        ) : error ? (
          <div className="w-full h-full grid place-items-center text-sm text-red-600">{error.message}</div>
        ) : isEmpty ? (
          <div className="w-full h-full grid place-items-center text-sm text-gray-500">No ELC-Cap rows for this scenario.</div>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>

      {/* “How many more by lastYear vs firstYear?” summary */}
      {counts && (
        <div className="mt-6 bg-blue-50 p-3 rounded-lg lg:w-3/5 sm:w-full align-center m-auto">
          <h4 className="text-xs font-medium text-blue-800 mb-2">
            Additional infrastructure needed by {counts.years.lastYear} vs {counts.years.firstYear}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {/* Offshore wind turbines */}
            <div className="flex justify-between">
              <span>Offshore wind turbines</span>
              <span className="font-semibold">{fmtInt(counts.units.OFF_WIND)}</span>
            </div>
            {/* Onshore wind turbines */}
            <div className="flex justify-between">
              <span>Onshore wind turbines</span>
              <span className="font-semibold">{fmtInt(counts.units.ON_WIND)}</span>
            </div>
            {/* Solar panels (millions) */}
            <div className="flex justify-between">
              <span>Solar panels</span>
              <span className="font-semibold">
                {fmtMillions(counts.units.SOLAR)}
              </span>
            </div>
            {/* Nuclear reactors */}
            <div className="flex justify-between">
              <span>Nuclear reactors</span>
              <span className="font-semibold">
                {Number.isFinite(counts.units.NUCLEAR)
                  ? (Math.round(counts.units.NUCLEAR * 10) / 10).toLocaleString()
                  : "—"}
              </span>
            </div>
            {/* Biomass units */}
            <div className="flex justify-between">
              <span>Biomass power units</span>
              <span className="font-semibold">
                {Number.isFinite(counts.units.BIOMASS)
                  ? (Math.round(counts.units.BIOMASS * 10) / 10).toLocaleString()
                  : "—"}
              </span>
            </div>
          </div>

          <div className="mt-2 text-[11px] text-blue-900/70">
            Assumptions: onshore turbine 3&nbsp;MW, offshore 14&nbsp;MW, solar panel 400&nbsp;W,
            nuclear reactor 1600&nbsp;MW, biomass unit 600&nbsp;MW. Tidal/wave/hydro excluded.
          </div>
        </div>
      )}
    </div>
  );
}
